import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Check, Plus, CaretUp, CaretDown, WarningCircle, Sparkle, Info, FileArrowDown, CircleNotch, FileDashed, Trash } from '@phosphor-icons/react';
import { useProjects } from '../hooks/useProjects';
import { useCategories } from '../hooks/useCategories';
import { useHaptic } from '../../../hooks/useHaptic';
import { TableRowSkeleton } from '../../../components/ui/Skeleton';
import { Sheet } from '../../../components/ui/Sheet';
import { Button } from '../../../components/ui/Button';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Pagination } from '../../../components/ui/Pagination';
import { DocumentUploadPanel } from './DocumentUploadPanel';
import { CreateProjectModal } from './CreateProjectModal';
import { projectService } from '../../../services/project.service';
import type { ProjectExtendedFields } from '../../../services/project.service';

function DocumentDownloadRow({
  filename,
  s3Key,
  causeId,
  isAdmin,
  onDeleted,
}: {
  filename: string;
  s3Key: string;
  causeId: string;
  isAdmin: boolean;
  onDeleted: (key: string) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // The bucket is VPC-only — stream through the backend proxy.
      await projectService.downloadProjectDocument(causeId, s3Key, filename);
    } catch (err: any) {
      alert(err.message || 'Failed to download file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteClick = () => {
    setConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setConfirmDelete(false);
    try {
      await projectService.deleteProjectDocument(causeId, s3Key);
      onDeleted(s3Key);
    } catch (err: any) {
      alert(err.message || 'Failed to delete file. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center justify-between bg-slate-50 border border-slate-200/60 px-3 py-2 rounded-xl text-[11px] group hover:border-accent/30 hover:bg-accent/5 transition-all">
      <div className="flex items-center gap-2 min-w-0">
        <FileArrowDown className="w-4 h-4 text-accent shrink-0" weight="duotone" />
        <span className="font-medium text-slate-700 truncate" title={filename}>{filename}</span>
      </div>
      <div className="flex items-center gap-1.5 ml-3 shrink-0">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || deleting}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-[10px] font-semibold text-slate-600 hover:border-accent hover:text-accent hover:bg-accent/5 disabled:opacity-50 transition-all"
          title="Download file"
        >
          {downloading
            ? <CircleNotch className="w-3 h-3 animate-spin" />
            : <FileArrowDown className="w-3 h-3" weight="bold" />}
          {downloading ? 'Loading...' : 'Download'}
        </button>

        {isAdmin && !confirmDelete && (
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={downloading || deleting}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-rose-200 text-[10px] font-semibold text-rose-500 hover:bg-rose-50 hover:border-rose-400 disabled:opacity-50 transition-all"
            title="Delete file"
          >
            {deleting
              ? <CircleNotch className="w-3 h-3 animate-spin" />
              : <Trash className="w-3 h-3" weight="bold" />}
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        )}

        {isAdmin && confirmDelete && (
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-bold text-rose-600">Sure?</span>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              className="px-2 py-1 rounded-lg bg-rose-500 text-white text-[10px] font-bold hover:bg-rose-600 transition-all"
            >
              Yes, delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-[10px] font-semibold text-slate-600 hover:border-slate-400 transition-all"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoTooltip({ text, position = 'top', align = 'center' }: { text: string; position?: 'top' | 'bottom'; align?: 'center' | 'right' | 'left' }) {
  const alignClasses = {
    center: "right-1/2 translate-x-1/2",
    right: "right-0 translate-x-0",
    left: "left-0 translate-x-0"
  }[align];
  
  const arrowAlign = {
    center: "left-1/2 -translate-x-1/2",
    right: "right-2",
    left: "left-2"
  }[align];

  const posClasses = position === 'top' 
    ? `bottom-full mb-2 origin-bottom ${alignClasses}`
    : `top-full mt-2 origin-top ${alignClasses}`;
    
  const arrowPos = position === 'top'
    ? `top-full border-t-white ${arrowAlign}`
    : `bottom-full border-b-white ${arrowAlign}`;

  return (
    <div className="group/tip relative inline-flex ml-1.5 align-middle">
      <Info size={14} weight="bold" className="text-slate-400 hover:text-accent cursor-help transition-colors" />
      <div className={`pointer-events-none absolute z-[100] w-64 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-150 ${posClasses}`}>
        <div className="bg-white text-slate-800 border border-slate-200 text-[11px] font-medium leading-relaxed rounded-lg px-3 py-2 shadow-xl text-center">
          {text}
        </div>
        <div className={`absolute border-4 border-transparent ${arrowPos}`} />
      </div>
    </div>
  );
}

interface ProjectsViewProps {
  onBack: () => void;
}

export function ProjectsView({ onBack }: ProjectsViewProps) {
  const navigate = useNavigate();
  const {  mediumTap } = useHaptic();
  const limit = 25;

  const {
    searchTerm,
    handleSearchChange,
    projectsList,
    loading,
    error,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSort,
    selectedProject,
    setSelectedProject,
    handleSelectProject,
    category,
    setCategory,
    subCategory,
    setSubCategory,
    summary,
    setSummary,
    objective,
    setObjective,
    remarks,
    setRemarks,
    jsonText,
    setJsonText,
    jsonStatus,
    setJsonStatus,
    setFilledByAI,
    setDocumentText,
    sessionAiProposed,
    setSessionAiProposed,
    saving,
    saveSuccess,
    saveError,
    handleSaveChanges,
    fetchProjects,
  } = useProjects(limit);

  const { categoryMap, isPending } = useCategories();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Role-based access: delete button only shown to admins.
  const isAdmin = localStorage.getItem('role') === 'admin';

  const jsonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // AI-derived supplementary fields (keywords, budget, timeline, etc.) from
  // the most recent document analysis. These are already persisted to
  // ai_metadata by the backend as soon as analysis completes, so this state
  // is purely for display/confirmation -- it isn't part of the save payload.
  const [aiExtras, setAiExtras] = useState<ProjectExtendedFields | null>(null);

  // Set when the AI's category/sub_category answer isn't in categoryMap, so
  // it was left for the user to pick instead of being silently blanked.
  const [offListWarning, setOffListWarning] = useState<string | null>(null);

  // Optimistically remove a deleted document from the open project's document list
  // without requiring a full refetch.
  const handleDocumentDeleted = (deletedKey: string) => {
    if (!selectedProject) return;
    setSelectedProject({
      ...selectedProject,
      documents: (selectedProject.documents || []).filter((k) => k !== deletedKey),
    });
  };

  // Autofocus the JSON field when drawer opens
  useEffect(() => {
    setOffListWarning(null);
    if (selectedProject) {
      const focusTimer = setTimeout(() => {
        jsonTextareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    } else {
      setAiExtras(null);
      setFilledByAI(false);
      setDocumentText(null);
    }
  }, [selectedProject]);

  const handleCategoryChange = (val: string) => {
    setCategory(val);
    setSubCategory('');
    setOffListWarning(null);
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    if (!text.trim()) {
      setJsonStatus('idle');
      return;
    }

    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') {
        setOffListWarning(null);
        const newCategory = parsed.category || parsed.Category || '';
        const newSubCategory = parsed.sub_category || parsed.subCategory || parsed.SubCategory || '';
        const newSummary = parsed.summary || parsed.Summary || '';
        const newObjective = parsed.objective || parsed.Objective || '';
        const newRemarks = parsed.remarks || parsed.Remarks || '';

        if (newCategory && categoryMap[newCategory]) {
            setCategory(newCategory);
            if (newSubCategory && categoryMap[newCategory].includes(newSubCategory)) {
                setSubCategory(newSubCategory);
            } else {
                // Previously this blanked silently, which is why every
                // Institutional Alumini Engagement classification vanished.
                setSubCategory('');
                if (newSubCategory) {
                    setOffListWarning(
                        `The AI answered "${newSubCategory}", which is not a valid sub-category for ${newCategory}. Please choose one.`
                    );
                }
            }
        } else {
            setCategory('');
            setSubCategory('');
            if (newCategory) {
                setOffListWarning(`The AI answered "${newCategory}", which is not a valid category. Please choose one.`);
            }
        }

        setSummary(newSummary);
        setObjective(newObjective);
        setRemarks(newRemarks);
        setJsonStatus('valid');
        // Deliberately not setFilledByAI(true) here: this handler backs the
        // general-purpose "JSON Autofill Quick Import" textarea below, which
        // anyone can paste hand-typed or copied-from-anywhere JSON into. The
        // ai_session flag tells the backend this save followed a real AI
        // analysis so it can record a replay case comparing the save against
        // ai_proposed; a manual paste has no ai_proposed to compare against and
        // would only pollute those replay cases (which Stage 4's safety check
        // relies on). The genuine AI path sets this flag itself, in
        // onAnalyzed below, after DocumentUploadPanel actually runs analysis.
      } else {
        setJsonStatus('invalid');
      }
    } catch {
      setJsonStatus('invalid');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'Enter')) {
      e.preventDefault();
      handleSaveChanges();
    }
  };

  

  const handleProjectClick = (p: any) => {
    mediumTap();
    handleSelectProject(p);
  };

  const getProjectStatus = (p: any) => {
    const fields = [p.category, p.sub_category, p.summary, p.objective, p.remarks];
    const filledCount = fields.filter((f) => !!f && f.trim() !== '').length;
    if (filledCount === 5) return 'configured';
    if (filledCount > 0) return 'in-progress';
    return 'pending';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 1 ? (
      <CaretUp size={11} weight="bold" className="shrink-0" />
    ) : (
      <CaretDown size={11} weight="bold" className="shrink-0" />
    );
  };

  return (
    <div className="space-y-6 font-sans">
      <PageHeader
        breadcrumb={[
          { label: 'Dashboard', onClick: onBack },
          { label: 'Projects' },
        ]}
        title="Causes"
        subtitle="Manage active project categories, summaries, donor contributions, and remarks."
      />

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col relative z-10">
        {/* Toolbar */}
        <div className="flex flex-col gap-4 p-5 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search bar */}
            <div className="relative w-full sm:max-w-md">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <MagnifyingGlass size={15} weight="bold" />
              </span>
              <input
                type="text"
                placeholder="Search by cause name or cause ID..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 transition-all cursor-pointer shrink-0"
              >
                <Plus className="w-4 h-4" weight="bold" />
                <span>New Project</span>
              </button>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider hidden sm:block">
                Total Projects: <span className="text-slate-800 font-extrabold">{totalItems}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                {[
                  { label: 'Cause ID', field: 'cause_id', className: 'pb-3 pl-5 w-[8%]' },
                  { label: 'Name', field: 'cause_name', className: 'pb-3 w-[18%]' },
                  { label: 'Contributors', field: 'contributor_count', className: 'pb-3 pr-4 text-right w-[10%]' },
                  { label: 'Amount Raised', field: 'total_amount', className: 'pb-3 pr-4 text-right w-[14%]' },
                  { label: 'Category', field: 'category', className: 'pb-3 pl-2 w-[16%]' },
                  { label: 'Sub-Category', field: 'sub_category', className: 'pb-3 w-[20%]' },
                  { label: 'Modified', field: 'last_modified', className: 'pb-3 pr-5 text-right w-[14%]' },
                ].map(({ label, field, className }) => (
                  <th
                    key={field}
                    className={`${className} cursor-pointer hover:text-slate-700 transition-colors`}
                    onClick={() => handleSort(field)}
                  >
                    <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
                      {label} {renderSortIcon(field)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 text-xs font-sans">
              {loading ? (
                <TableRowSkeleton cols={7} rows={10} />
              ) : error ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-rose-500 font-semibold">{error}</td>
                </tr>
              ) : projectsList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-400 font-medium">No campaigns found matching your search.</td>
                </tr>
              ) : (
                projectsList.map((project) => {
                  const donorCount = Object.keys(project.donor_ids || {}).length;
                  const status = getProjectStatus(project);

                  return (
                    <tr
                      key={project._id}
                      onClick={() => handleProjectClick(project)}
                      className="hover:bg-slate-50/80 transition-smooth group cursor-pointer"
                    >
                      {/* Cause ID */}
                      <td className="py-4 pl-5">
                        <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md">
                          {project.cause_id || 'NO ID'}
                        </span>
                      </td>

                      {/* Name + Status */}
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-slate-800 group-hover:text-accent transition-colors leading-snug line-clamp-2">
                            {project.cause_name}
                          </p>
                          {status === 'configured' && (
                            <span className="flex items-center gap-1 text-[8px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                              <span className="w-1 h-1 rounded-full bg-emerald-500 inline-block" />
                              Done
                            </span>
                          )}
                          {status === 'in-progress' && (
                            <span className="flex items-center gap-1 text-[8px] font-bold text-amber-600 bg-amber-50 border border-amber-200/60 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                              <span className="w-1 h-1 rounded-full bg-amber-500 inline-block" />
                              WIP
                            </span>
                          )}
                          {status === 'pending' && (
                            <span className="flex items-center gap-1 text-[8px] font-bold text-slate-400 bg-slate-50 border border-slate-200/60 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                              <span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />
                              New
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Contributor Base */}
                      <td className="py-4 pr-4 text-right">
                        <span className="font-bold text-slate-700">{donorCount}</span>
                      </td>

                      {/* Amount Raised */}
                      <td className="py-4 pr-4 text-right">
                        <span className="font-extrabold text-slate-900">
                          {formatCurrency(project.total_amount || 0)}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="py-4 pl-2">
                        {project.category ? (
                          <span className="text-[9px] font-bold text-accent bg-accent-light border border-accent/20 px-2 py-0.5 rounded-full uppercase tracking-wider">
                            {project.category}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[10px] font-medium">—</span>
                        )}
                      </td>

                      {/* Sub-Category */}
                      <td className="py-4">
                        <p className="font-medium text-slate-600 leading-snug truncate max-w-[200px] hover:max-w-full transition-all duration-100 text-[11px]">
                          {project.sub_category || <span className="text-slate-300">—</span>}
                        </p>
                      </td>

                      {/* Modified */}
                      <td className="py-4 pr-5 text-right text-slate-500 font-medium">
                        {project.last_modified
                          ? new Date(project.last_modified).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !error && projectsList.length > 0 && (
          <div className="px-5 pb-5">
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={limit}
              itemLabel="campaign"
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}
      </div>


      {/* Drawer Configuration Drawer using updated Sheet component */}
      <Sheet
        isOpen={selectedProject !== null}
        onClose={() => setSelectedProject(null)}
        title="Campaign Configuration"
        maxWidth="max-w-2xl"
      >
        {selectedProject && (
          <div 
            onKeyDown={handleKeyDown}
            className="space-y-6 font-sans"
          >
            {/* Campaign Profile Card */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono font-bold text-slate-505 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md">
                    Cause ID: {selectedProject.cause_id || 'N/A'}
                  </span>
                  {selectedProject.category && (
                    <span className="text-[9px] font-bold text-accent bg-accent-light border border-accent/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                      {selectedProject.category}
                    </span>
                  )}
                </div>
              </div>
              <h3 className="font-outfit font-extrabold text-2xl text-slate-900 leading-tight">
                {selectedProject.cause_name}
              </h3>
            </div>

            {/* Raised Summary Info */}
            <div className="grid grid-cols-2 gap-4 bg-surface-hover p-4 rounded-2xl border border-border">
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Total Amount Raised</span>
                <span className="font-outfit font-extrabold text-slate-950 text-xl leading-none">
                  {formatCurrency(selectedProject.total_amount || 0)}
                </span>
              </div>
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Contributor Base</span>
                <span className="font-bold text-slate-800 text-sm">
                  {Object.keys(selectedProject.donor_ids || {}).length} Contributor{Object.keys(selectedProject.donor_ids || {}).length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Contributor Distributions */}
            {Object.keys(selectedProject.donor_ids || {}).length > 0 && (
              <div className="space-y-3">
                <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Donor Giving Breakdown</h4>
                <div className="border border-slate-200/60 rounded-2xl overflow-hidden max-h-[140px] overflow-y-auto shadow-xs">
                  <table className="w-full border-collapse text-left text-xs bg-white">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-bold uppercase text-[9px]">
                        <th className="py-2.5 px-3">Donor ID</th>
                        <th className="py-2.5 px-3 text-right">Amount Contributed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 text-[11px] font-medium text-slate-600">
                      {Object.entries(selectedProject.donor_ids).map(([donorId, amt]) => (
                        <tr key={donorId} className="hover:bg-slate-50/50">
                          <td className="py-2 px-3 font-mono">{donorId}</td>
                          <td className="py-2 px-3 text-right font-extrabold text-slate-800">{formatCurrency(amt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Supporting Documents */}
            {selectedProject.documents && selectedProject.documents.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FileDashed className="w-3.5 h-3.5" weight="fill" />
                  Supporting Documents
                </h4>
                <div className="space-y-1.5">
                  {selectedProject.documents.map((key) => {
                    const filename = key.split('/').pop() || key;
                    // Strip the timestamp suffix for a cleaner display name
                    const displayName = filename.replace(/_\d{8}T\d{6}(\.[^.]+)?$/, '$1') || filename;
                    return (
                      <DocumentDownloadRow
                        key={key}
                        filename={displayName}
                        s3Key={key}
                        causeId={selectedProject.cause_id}
                        isAdmin={isAdmin}
                        onDeleted={handleDocumentDeleted}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            <hr className="border-slate-100" />

            {/* Configuration Form */}
            <div className="space-y-4">
              <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Configure Campaign Attributes</h4>

              {/* AI Document Analysis — uploads feed the same autofill path as JSON paste below */}
              <DocumentUploadPanel
                projectId={selectedProject._id}
                onAnalyzed={(coreFieldsJson: string, extendedFields: ProjectExtendedFields, documentText: string) => {
                  handleJsonChange(coreFieldsJson);
                  setAiExtras(extendedFields);
                  setFilledByAI(true);
                  setDocumentText(documentText || null);
                  try {
                    setSessionAiProposed(JSON.parse(coreFieldsJson));
                  } catch(e) {}
                }}
              />

              {aiExtras && (
                <div className="bg-accent-light/40 border border-accent/20 p-3 rounded-2xl space-y-2">
                  <span className="text-[9px] font-bold text-accent uppercase tracking-wider block">
                    AI Insights (saved automatically)
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {[...aiExtras.keywords, ...aiExtras.organizations, ...aiExtras.locations, ...aiExtras.sdgs].map((tag, i) => (
                      <span key={i} className="text-[9px] font-semibold text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  {(aiExtras.budget || aiExtras.timeline) && (
                    <div className="flex gap-4 text-[10px] text-slate-600 font-medium">
                      {aiExtras.budget && <span><strong>Budget:</strong> {aiExtras.budget}</span>}
                      {aiExtras.timeline && <span><strong>Timeline:</strong> {aiExtras.timeline}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* JSON Quick-Fill Area */}
              <div className="bg-surface-hover border border-border p-4 rounded-2xl space-y-2 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-[9px] font-bold text-slate-450 uppercase tracking-wider block">
                      JSON Autofill Quick Import
                    </label>
                    <span className="text-[9px] text-slate-400 font-medium">Pasting JSON instantly pre-fills form fields (Press Ctrl+Enter / Ctrl+S to Save)</span>
                  </div>
                  {jsonStatus === 'valid' && (
                    <span className="text-[8px] font-bold text-emerald-650 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full animate-pulse uppercase tracking-wider">
                      ✓ Pre-filled!
                    </span>
                  )}
                  {jsonStatus === 'invalid' && (
                    <span className="text-[8px] font-bold text-rose-500 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                      ✗ Invalid JSON
                    </span>
                  )}
                </div>
                <textarea
                  ref={jsonTextareaRef}
                  value={jsonText}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  rows={3}
                  placeholder='Paste JSON here, e.g. {"category": "Faculty", "sub_category": "Endowed Chairs...", "summary": "...", "objective": "...", "remarks": "..."}'
                  className="w-full font-mono bg-white border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 outline-none rounded-xl px-3 py-2 text-[10px] text-slate-700 transition-smooth placeholder-slate-400"
                />
              </div>

              {/* Off-list AI answer warning */}
              {offListWarning && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <WarningCircle size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[10px] font-semibold text-amber-700 leading-snug">{offListWarning}</p>
                </div>
              )}

              {/* Dropdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Category</label>
                  <select
                    value={category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-3 py-2 text-xs text-slate-700 transition-smooth"
                  >
                    <option value="">-- Select Category --</option>
                    {Object.keys(categoryMap).map((catName) => (
                      <option key={catName} value={catName}>
                        {catName}{isPending(catName) ? ' (Pending Review)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Sub-Category</label>
                  <select
                    value={subCategory}
                    onChange={(e) => {
                      setSubCategory(e.target.value);
                      setOffListWarning(null);
                    }}
                    disabled={!category}
                    className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-3 py-2 text-xs text-slate-700 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">-- Select Sub-Category --</option>
                    {category && categoryMap[category] &&
                      categoryMap[category].map((subName) => (
                        <option key={subName} value={subName}>
                          {subName}{isPending(category, subName) ? ' (Pending Review)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Text fields */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Summary</label>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={3}
                    placeholder="Enter a brief summary of the campaign/project..."
                    className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-3 py-2 text-xs text-slate-700 transition-smooth placeholder-slate-400"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Objective</label>
                  <textarea
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    rows={2}
                    placeholder="What is the objective or target of this campaign..."
                    className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-3 py-2 text-xs text-slate-700 transition-smooth placeholder-slate-400"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Remarks</label>
                  <input
                    type="text"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add any internal remarks or tags..."
                    className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-3 py-2.5 text-xs text-slate-700 transition-smooth placeholder-slate-400"
                  />
                </div>
              </div>
            </div>

            {/* Status Feedbacks */}
            {saveSuccess && (
              <div className="p-3 bg-emerald-50 border border-emerald-255 text-emerald-600 rounded-xl text-xs font-semibold text-center animate-fade-in flex items-center justify-center gap-1.5">
                <Check size={14} weight="bold" />
                Campaign details saved successfully! Closing editor...
              </div>
            )}
            {saveError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-600 rounded-xl text-xs font-semibold text-center">
                Error: {saveError}
              </div>
            )}

            {/* AI Correction Indicator */}
            {sessionAiProposed && (
              category !== (sessionAiProposed.category || '') ||
              subCategory !== (sessionAiProposed.sub_category || '') ||
              summary !== (sessionAiProposed.summary || '') ||
              objective !== (sessionAiProposed.objective || '') ||
              remarks !== (sessionAiProposed.remarks || '')
            ) && (
              <div className="shrink-0 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-start gap-2.5 bg-accent/5 border border-accent/20 rounded-xl p-3">
                  <Sparkle className="w-4 h-4 text-accent shrink-0 mt-0.5" weight="fill" />
                  <div>
                    <p className="text-[11px] font-bold text-accent leading-tight">Correct AI prompt</p>
                    <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">
                      You've modified fields that were pre-filled by the AI. When you save, the system will automatically learn from your corrections to improve future analysis.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Buttons — the AI check entry point sits on the left, away from the
                destructive/primary actions, so it reads as navigation rather than a save. */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 shrink-0">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => navigate('/projects/ai-settings')}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-accent transition-smooth"
                >
                  <Sparkle size={14} weight="fill" />
                  AI check &amp; prompt settings
                </button>
                <InfoTooltip text="View and edit the prompt, category list, and the rules automatically learned from past corrections." position="top" align="left" />
              </div>

              <div className="flex items-center gap-3">
              <Button
                onClick={() => setSelectedProject(null)}
                disabled={saving}
                variant="outline"
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={saving || saveSuccess}
                variant="accent"
                isLoading={saving}
                className="px-5 py-2 font-bold shadow-sm"
              >
                Save Configuration
              </Button>
              </div>
            </div>
          </div>
        )}
      </Sheet>

      {/* Create Project with AI Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setPage(1);
          fetchProjects();
        }}
      />


    </div>
  );
}
